import { t } from 'i18next';
import { AlertTriangle, Check, ChevronDown } from 'lucide-react';

import { Card } from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { AdminHealthOverview, AdminSystemJob } from '@activepieces/shared';

const EMPTY_VALUE = '—';

const JobLine = ({ job }: { job: AdminSystemJob }) => (
  <div className="flex items-baseline justify-between gap-4 text-sm">
    <span className="flex items-baseline gap-2 min-w-0">
      <span className={job.isOverdue ? 'text-destructive font-medium' : ''}>
        {job.name}
      </span>
      <span className="font-mono text-xs text-muted-foreground shrink-0">
        {job.cron || EMPTY_VALUE}
      </span>
    </span>
    <span className="flex items-baseline gap-2 shrink-0 text-xs text-muted-foreground">
      {job.lastFailureAt && (
        <span title={job.lastFailureReason ?? ''}>
          {t('gave up {times}x', { times: job.failureCount })}
        </span>
      )}
      {job.nextRunAt ? new Date(job.nextRunAt).toLocaleString() : EMPTY_VALUE}
    </span>
  </div>
);

// The signal here is binary — the scheduler is turning, or it stopped — so the
// headline is one line and the per-job detail stays folded away.
export const SystemJobsPanel = ({
  systemJobs,
}: {
  systemJobs: AdminHealthOverview['systemJobs'];
}) => {
  if (systemJobs.length === 0) {
    return null;
  }

  const overdue = systemJobs.filter((job) => job.isOverdue);
  const failing = systemJobs
    .filter((job) => job.lastFailureAt !== null)
    .sort((left, right) =>
      (right.lastFailureAt ?? '').localeCompare(left.lastFailureAt ?? ''),
    );
  const lastFailure = failing.at(0);
  const isStalled = overdue.length > 0;

  return (
    <Card
      className={
        isStalled
          ? 'flex flex-col overflow-hidden border-destructive/30'
          : 'flex flex-col overflow-hidden'
      }
    >
      <Collapsible>
        <CollapsibleTrigger
          aria-label={t('Show every scheduled job')}
          className={
            isStalled
              ? 'group w-full text-left flex items-start gap-2.5 px-4 py-3 bg-destructive/5 hover:bg-destructive/10 transition-colors'
              : 'group w-full text-left flex items-start gap-2.5 px-4 py-3 hover:bg-muted/40 transition-colors'
          }
        >
          {isStalled ? (
            <AlertTriangle className="size-4 text-destructive shrink-0 mt-0.5" />
          ) : (
            <Check className="size-4 text-success shrink-0 mt-0.5" />
          )}
          <div className="min-w-0 flex-1 flex flex-col gap-0.5">
            <p
              className={
                isStalled ? 'text-sm font-medium text-destructive' : 'text-sm'
              }
            >
              {isStalled
                ? t('Scheduler stalled on: {jobs}', {
                    jobs: overdue.map((job) => job.name).join(', '),
                  })
                : t('{scheduled} scheduled jobs, all on time', {
                    scheduled: systemJobs.length,
                  })}
            </p>
            {/* History, not current state: BullMQ keeps these entries for a
                month, and the ten retries span five days, so the date is when
                the job gave up — the trouble started well before it. Letting
                this colour the card would keep the page red for weeks. */}
            {lastFailure && (
              <p className="text-xs text-muted-foreground">
                {t(
                  'Gave up {times}x in the last 30 days: {job}, latest {date}',
                  {
                    times: lastFailure.failureCount,
                    job: lastFailure.name,
                    date: new Date(
                      lastFailure.lastFailureAt ?? '',
                    ).toLocaleString(),
                  },
                )}
                {failing.length > 1 &&
                  t(' — and {others} others', { others: failing.length - 1 })}
              </p>
            )}
            {/* Without this, the date below reads as "last run", which it is not
              — and a reader would trust a green line over a dead cron. */}
            <p className="text-xs text-muted-foreground">
              {t(
                'Successful runs are deleted on completion, so there is no "last succeeded" to show: a next run stuck in the past is what proves the scheduler stopped.',
              )}
            </p>
          </div>
          <ChevronDown className="size-5 shrink-0 self-center text-muted-foreground group-hover:text-foreground group-data-[state=open]:rotate-180 transition-transform" />
        </CollapsibleTrigger>

        <CollapsibleContent className="overflow-hidden">
          <div className="flex flex-col gap-1.5 px-4 py-3 border-t">
            {systemJobs.map((job) => (
              <JobLine key={job.name} job={job} />
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};
